import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [tokenDialog, setTokenDialog] = useState({ open: false, token: '', clientName: '' });
  const [formData, setFormData] = useState({
    name: '',
    tokens: '',
    localization: 'en'
  });
  const { user } = useAuth();

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/clients');
      const clientsWithId = response.data.map(client => ({
        ...client,
        id: client.client_id
      }));
      setClients(clientsWithId);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({ 
        name: client.name,
        tokens: client.tokens.toString(),
        localization: client.localization
      });
    } else {
      setEditingClient(null);
      setFormData({ 
        name: '',
        tokens: '',
        localization: 'en'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
    setFormData({ 
      name: '',
      tokens: '',
      localization: 'en'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        // Update client name and localization
        await axios.put(`/admin/clients/${editingClient.client_id}`, {
          name: formData.name,
          localization: formData.localization
        });
        
        // Update client tokens if changed
        const newTokens = parseInt(formData.tokens);
        if (newTokens !== editingClient.tokens) {
          await axios.put(`/admin/clients/${editingClient.client_id}/tokens`, {
            tokens: newTokens
          });
        }
      } else {
        await axios.post('/admin/clients', {
          name: formData.name,
          localization: formData.localization
        });
      }
      handleCloseDialog();
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`/admin/clients/${clientId}`);
        fetchClients();
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to delete client');
      }
    }
  };

  const getTokenColor = (tokens) => {
    if (tokens > 100) return 'success';
    if (tokens > 50) return 'warning';
    return 'error';
  };

  const columns = [
    { field: 'client_id', headerName: 'ID', flex: 0.5, minWidth: 60 },
    { field: 'name', headerName: 'Client Name', flex: 1.5, minWidth: 150 },
    { field: 'localization', headerName: 'Localization', flex: 0.8, minWidth: 100 },
    { 
      field: 'tokens', 
      headerName: 'Token Balance', 
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          icon={<AccountBalanceWalletIcon />}
          label={params.value}
          color={getTokenColor(params.value)}
          variant="outlined"
        />
      )
    },
    { 
      field: 'access_token', 
      headerName: 'Access Token', 
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              flex: 1,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              color: 'text.primary'
            }}
          >
            {params.value}
          </Typography>
          <Tooltip title="View full token">
            <IconButton
              size="small"
              onClick={() => setTokenDialog({ 
                open: true, 
                token: params.value, 
                clientName: params.row.name 
              })}
              color="primary"
              sx={{ 
                minWidth: 'auto',
                p: 0.5
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    },
    { 
      field: 'created_at', 
      headerName: 'Created At', 
      flex: 1.2,
      minWidth: 150,
      valueGetter: (params) => new Date(params.row.created_at).toLocaleString()
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.client_id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Integration Clients</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Client
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={clients}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          checkboxSelection
          disableSelectionOnClick
          loading={loading}
          getRowHeight={() => 'auto'}
          autoWidth
          sx={{
            '& .MuiDataGrid-cell': {
              py: 2
            },
            '& .MuiDataGrid-columnHeader': {
              flex: 1,
            },
            '& .MuiDataGrid-cell': {
              flex: 1,
            },
          }}
        />
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingClient ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              autoFocus
              label="Client Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              label="Localization"
              fullWidth
              value={formData.localization}
              onChange={(e) => setFormData({ ...formData, localization: e.target.value })}
              helperText="Enter the localization code (e.g., 'en', 'es', 'fr')"
              placeholder="en"
              required
            />
            {editingClient && (
              <TextField
                label="Token Balance"
                type="number"
                fullWidth
                value={formData.tokens}
                onChange={(e) => setFormData({ ...formData, tokens: e.target.value })}
                helperText="Enter the new token balance for this client"
                required
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingClient ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tokenDialog.open} onClose={() => setTokenDialog({ ...tokenDialog, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Access Token for {tokenDialog.clientName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Full Access Token:
              </Typography>
              <TextField
                fullWidth
                value={tokenDialog.token}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    backgroundColor: 'background.paper',
                    color: 'text.primary'
                  }
                }}
                multiline
                rows={3}
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Tooltip title="Copy to clipboard">
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    navigator.clipboard.writeText(tokenDialog.token);
                    // You could add a snackbar notification here
                  }}
                  sx={{ minWidth: 120 }}
                >
                  Copy Token
                </Button>
              </Tooltip>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenDialog({ ...tokenDialog, open: false })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminClients; 